import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import api from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ArrowRight, Eye, EyeOff, ShieldCheck, Mail, Trash2, Plus, BellRing, Send } from "lucide-react";

export default function AdminAccount() {
    const { user } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);

    // Notification recipients
    const [emails, setEmails] = useState([]);
    const [enabled, setEnabled] = useState(true);
    const [newEmail, setNewEmail] = useState("");
    const [notifLoading, setNotifLoading] = useState(true);
    const [savingNotif, setSavingNotif] = useState(false);
    const [testingNotif, setTestingNotif] = useState(false);

    useEffect(() => {
        api.get("/admin/settings/notifications")
            .then((r) => { setEmails(r.data.emails || []); setEnabled(r.data.enabled !== false); })
            .catch(() => toast.error("Couldn't load notification settings"))
            .finally(() => setNotifLoading(false));
    }, []);

    const addEmail = () => {
        const e = newEmail.trim().toLowerCase();
        if (!e) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error("Enter a valid email"); return; }
        if (emails.includes(e)) { toast.error("Already in the list"); return; }
        setEmails([...emails, e]); setNewEmail("");
    };
    const removeEmail = (e) => setEmails(emails.filter((x) => x !== e));

    const saveNotif = async () => {
        setSavingNotif(true);
        try {
            await api.put("/admin/settings/notifications", { emails, enabled });
            toast.success("Notification settings saved.");
        } catch { toast.error("Save failed"); }
        finally { setSavingNotif(false); }
    };

    const testNotif = async () => {
        if (emails.length === 0) { toast.error("Add at least one recipient first."); return; }
        setTestingNotif(true);
        try {
            await api.post("/admin/settings/notifications/test");
            toast.success(`Test sent to ${emails.length} recipient${emails.length > 1 ? "s" : ""}.`);
        } catch { toast.error("Test failed. Check backend logs."); }
        finally { setTestingNotif(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (!/[A-Za-z]/.test(next) || !/\d/.test(next)) { toast.error("Use letters and at least one number."); return; }
        if (next !== confirm) { toast.error("New passwords don't match."); return; }
        if (next === current) { toast.error("New password must be different from your current one."); return; }
        setLoading(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            toast.success("Password updated. You're still signed in.");
            setCurrent(""); setNext(""); setConfirm("");
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Couldn't update password.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-10" data-testid="admin-account">
            <div>
                <div className="tracked text-coffee/50 text-[10px] mb-3">Settings</div>
                <h1 className="font-editorial font-bold text-coffee text-4xl md:text-5xl leading-tight">
                    Account & Security.
                </h1>
                <p className="font-body text-coffee/60 mt-3 max-w-2xl">
                    Manage your sign-in, change your password, and choose who gets emailed when someone reaches out.
                </p>
            </div>

            {/* Account card */}
            <div className="bg-white border border-coffee/10 p-6 md:p-8 max-w-2xl">
                <div className="flex items-start gap-4 mb-2">
                    <ShieldCheck size={22} strokeWidth={1.3} className="text-gold mt-1" />
                    <div>
                        <div className="tracked text-coffee/50 text-[10px]">Signed In As</div>
                        <div className="font-editorial font-medium text-coffee text-xl mt-1" data-testid="account-email">{user?.email}</div>
                        <div className="font-mono text-[10px] text-coffee/40 mt-1">Role: {user?.role}</div>
                    </div>
                </div>
            </div>

            {/* Notification recipients */}
            <div className="bg-white border border-coffee/10 p-6 md:p-8 max-w-2xl" data-testid="notification-card">
                <div className="flex items-start gap-4 mb-8">
                    <BellRing size={22} strokeWidth={1.3} className="text-gold mt-1" />
                    <div className="flex-1">
                        <h2 className="font-editorial font-bold text-coffee text-2xl">Form Submission Alerts</h2>
                        <p className="font-body text-coffee/60 text-sm mt-1 leading-relaxed">
                            Every time someone submits a Contact / Partner / Career / Newsletter form, we'll email these recipients.
                        </p>
                    </div>
                </div>

                <label className="flex items-center gap-3 cursor-pointer mb-6">
                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="notif-enabled" className="accent-coffee" />
                    <span className="tracked text-coffee/80 text-[11px]">{enabled ? "Notifications enabled" : "Notifications disabled"}</span>
                </label>

                {notifLoading ? (
                    <div className="tracked text-coffee/40 text-[10px]">Loading…</div>
                ) : (
                    <>
                        <div className="space-y-2 mb-5">
                            {emails.length === 0 && (
                                <div className="text-coffee/40 font-body text-sm italic">No recipients yet. Add one below.</div>
                            )}
                            {emails.map((e) => (
                                <div key={e} className="flex items-center justify-between bg-cream-light px-4 py-3 border border-coffee/10" data-testid={`notif-email-${e}`}>
                                    <div className="flex items-center gap-3">
                                        <Mail size={14} className="text-gold" />
                                        <span className="font-body text-coffee">{e}</span>
                                    </div>
                                    <button onClick={() => removeEmail(e)} className="text-coffee/40 hover:text-destructive p-1" aria-label="Remove" data-testid={`notif-remove-${e}`}>
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 mb-6">
                            <input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                                placeholder="someone@cafehideout.com"
                                data-testid="notif-new-email"
                                className="flex-1 bg-cream-light border border-coffee/15 focus:border-coffee px-4 py-3 text-coffee font-body outline-none"
                            />
                            <button onClick={addEmail} className="btn-pill btn-pill-coffee whitespace-nowrap" data-testid="notif-add">
                                <Plus size={14} /> Add Recipient
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-3 pt-4 border-t border-coffee/10">
                            <button onClick={saveNotif} disabled={savingNotif} className="btn-pill btn-pill-coffee disabled:opacity-50" data-testid="notif-save">
                                {savingNotif ? "Saving…" : "Save Recipients"} <ArrowRight size={14} />
                            </button>
                            <button onClick={testNotif} disabled={testingNotif} className="inline-flex items-center gap-2 tracked text-[10px] px-5 py-3 border border-gold text-gold hover:bg-gold hover:text-coffee transition-colors disabled:opacity-50" data-testid="notif-test">
                                <Send size={12} /> {testingNotif ? "Sending…" : "Send Test Email"}
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* Change password card */}
            <div className="bg-white border border-coffee/10 p-6 md:p-8 max-w-2xl">
                <div className="flex items-start gap-4 mb-8">
                    <KeyRound size={22} strokeWidth={1.3} className="text-gold mt-1" />
                    <div>
                        <h2 className="font-editorial font-bold text-coffee text-2xl">Change Password</h2>
                        <p className="font-body text-coffee/60 text-sm mt-1">
                            Minimum 8 characters · letters + a number
                        </p>
                    </div>
                </div>

                <form onSubmit={submit} className="space-y-7" data-testid="change-password-form">
                    <div>
                        <label className="tracked text-coffee/60 text-[10px] block mb-3">Current Password</label>
                        <div className="relative">
                            <input
                                type={showPwd ? "text" : "password"}
                                value={current}
                                onChange={(e) => setCurrent(e.target.value)}
                                data-testid="current-password"
                                className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 pr-10 text-coffee font-body outline-none"
                                required
                            />
                            <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-0 top-3 text-coffee/60" aria-label="toggle visibility">
                                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="tracked text-coffee/60 text-[10px] block mb-3">New Password</label>
                        <input
                            type={showPwd ? "text" : "password"}
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            data-testid="new-password"
                            className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none"
                            required
                            minLength={8}
                        />
                    </div>
                    <div>
                        <label className="tracked text-coffee/60 text-[10px] block mb-3">Confirm New Password</label>
                        <input
                            type={showPwd ? "text" : "password"}
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            data-testid="confirm-password"
                            className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-3 text-coffee font-body outline-none"
                            required
                            minLength={8}
                        />
                    </div>

                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn-pill btn-pill-coffee disabled:opacity-50"
                            data-testid="change-password-submit"
                        >
                            {loading ? "Updating…" : "Update Password"} <ArrowRight size={14} />
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
